import { formatPricingCheckoutTemplate } from '@/cms/pricing-checkout/runtime-copy';
import type {
  SiteRuntimePricing,
  SiteRuntimePricingPurchaseOptions,
} from '@/site-runtime/types';

type BillingCycle = 'month' | 'year';

export function findSiteRuntimePricingBillingOption<
  BillingOption extends { billingCycle: BillingCycle },
>(params: {
  billingCycle: BillingCycle;
  options: readonly BillingOption[];
}) {
  return params.options.find(
    (option) => option.billingCycle === params.billingCycle
  );
}

export function requireSiteRuntimeMonthlyBillingOption<
  BillingOption extends { billingCycle: BillingCycle },
>(params: {
  options: readonly BillingOption[];
  planKey: string;
}) {
  const option = findSiteRuntimePricingBillingOption({
    billingCycle: 'month',
    options: params.options,
  });
  if (!option) {
    throw new Error(
      `Published Pricing plan ${params.planKey} must include monthly billing.`
    );
  }
  return option;
}

export function calculateSiteRuntimeAnnualSavingsPercent(params: {
  monthlyAmountCents: number;
  yearlyAmountCents: number;
}) {
  const yearlyReferenceAmount = params.monthlyAmountCents * 12;
  if (yearlyReferenceAmount <= 0) {
    return 0;
  }

  return Math.max(
    0,
    Math.round(
      ((yearlyReferenceAmount - params.yearlyAmountCents) /
        yearlyReferenceAmount) *
        100
    )
  );
}

export function resolveSiteRuntimeMaximumAnnualDiscountLabel(params: {
  uiCopy: Pick<SiteRuntimePricing['uiCopy'], 'discountTemplate'>;
  plans: Array<{
    planKey: string;
    billingOptions: Array<
      Pick<
        SiteRuntimePricingPurchaseOptions['plans'][number]['billingOptions'][number],
        'amountCents' | 'billingCycle' | 'currency'
      >
    >;
  }>;
}) {
  const maximumSavingsPercent = params.plans.reduce((maximum, plan) => {
    const monthly = findSiteRuntimePricingBillingOption({
      billingCycle: 'month',
      options: plan.billingOptions,
    });
    const yearly = findSiteRuntimePricingBillingOption({
      billingCycle: 'year',
      options: plan.billingOptions,
    });
    if (!monthly || !yearly) {
      return maximum;
    }
    if (monthly.currency !== yearly.currency) {
      throw new Error(
        `Pricing plan ${plan.planKey} monthly and yearly prices must use the same currency.`
      );
    }

    return Math.max(
      maximum,
      calculateSiteRuntimeAnnualSavingsPercent({
        monthlyAmountCents: monthly.amountCents,
        yearlyAmountCents: yearly.amountCents,
      })
    );
  }, 0);

  return maximumSavingsPercent > 0
    ? formatPricingCheckoutTemplate(params.uiCopy.discountTemplate, {
        percent: maximumSavingsPercent,
      })
    : '';
}
